import { useCleanedAndSortedPurchables } from "@customer-app/state/reducers/purchasables";
import { OrgUtils, OutOfStockBehavior } from "@smartrr/shared/entities/Organization";
import { IUnavailableVariantsAndDeliveries } from "@smartrr/shared/entities/PurchaseState";
import { getPurchStFutureScheduledDeliveriesWithNonActive } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { getActiveAndPausedCustomerPurchaseStates } from "@smartrr/shared/utils/getActiveAndPausedCustomerPurchaseStates";
import { isLineItemInDelivery } from "@smartrr/shared/utils/isLineItemInDelivery";
import { useVariantToPurchasableMap } from "@smartrr/shared/utils/useVariantToPurchasableMap";
import { isPurchasableDeleted, isVariantDeleted, isVariantInStock } from "@smartrr/shared/utils/variants";
import { useMemo } from "react";

import { usePurchaseStates } from "../../AquariusInner/hooks/usePurchaseStates";
import { useOrganization } from "../../utils";

export default function useHandleUnavailableItemsAndDeliveries() {
  const purchaseStates = getActiveAndPausedCustomerPurchaseStates(usePurchaseStates());

  const organization = useOrganization();

  const orgUseOOSSettings = organization
    ? organization.outOfStockBehavior === OutOfStockBehavior.CREATE_ONLY_AVAILABLE_PAUSE ||
      organization.outOfStockBehavior === OutOfStockBehavior.CREATE_ONLY_AVAILABLE_SKIP
    : false;

  const purchasables = useCleanedAndSortedPurchables().purchasables;
  const variantToPurchasableMap = useVariantToPurchasableMap(purchasables);

  return useMemo(() => {
    const arr: IUnavailableVariantsAndDeliveries = { purchasables: [], vnts: [], deliveries: [] };

    for (const cps of purchaseStates) {
      const scheduledDeliveries = getPurchStFutureScheduledDeliveriesWithNonActive(
        cps,
        OrgUtils.getBillingSchedule(organization),
        1
      );

      const delivery = scheduledDeliveries?.length ? scheduledDeliveries[0] : undefined;

      if (organization && orgUseOOSSettings && delivery) {
        const lineItems = delivery.purchaseState.stLineItems;

        for (const lineItem of lineItems) {
          if (!isLineItemInDelivery(lineItem, delivery)) {
            continue;
          }

          const purchaseable = variantToPurchasableMap[lineItem.vnt.id];

          const isProductDeleted = isPurchasableDeleted(purchaseable);
          const isDeletedVariant = isVariantDeleted(lineItem.vnt);
          const isVntInStock = isVariantInStock(lineItem.vnt, lineItem.quantity);

          const flagDelivery = !isVntInStock || isDeletedVariant || isProductDeleted;

          if (isProductDeleted) {
            const isUniquePurchasable = !arr.purchasables.some(
              listedPurchasable => listedPurchasable.id === purchaseable.id
            );
            isUniquePurchasable && arr.purchasables.push(variantToPurchasableMap[lineItem.vnt.id]);
          }

          if (!isVntInStock || isDeletedVariant) {
            const isUniqueVariant = !arr.vnts?.some(vnt => vnt.id === lineItem.vnt.id);
            isUniqueVariant && arr.vnts.push(lineItem.vnt);
          }

          if (flagDelivery) {
            const isUniqueDelivery = !arr.deliveries.some(
              listedDelivery => listedDelivery.purchaseState.id === delivery.purchaseState.id
            );
            isUniqueDelivery && arr.deliveries.push(delivery);
          }
        }
      }
    }
    return arr;
  }, [organization, purchaseStates]);
}
